import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const DEMO = [
  { role: 'admin', email: 'admin@zayed.ae', password: 'Admin@2026' },
  { role: 'teacher', email: 'teacher@zayed.ae', password: 'Teacher@2026' },
  { role: 'student', email: 'student@zayed.ae', password: 'Student@2026' },
  { role: 'parent', email: 'parent@zayed.ae', password: 'Parent@2026' },
];

export default function Login() {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(t('welcome') + ' ' + u.full_name);
      navigate({ admin: '/admin', teacher: '/teacher', student: '/student', parent: '/parent' }[u.role] || '/');
    } catch (err) {
      // Differentiate between network/CORS/DNS failure (no response object) and API errors
      if (!err.response) {
        toast.error(window?.navigator?.language?.startsWith('ar')
          ? 'تعذّر الاتصال بالخادم. تحقّق من اتصال الإنترنت أو حاول لاحقاً.'
          : 'Cannot reach the server. Check your connection or try again later.');
      } else if (err.response.status === 401 || err.response.status === 400) {
        toast.error(err.response.data?.detail
          || (window?.navigator?.language?.startsWith('ar') ? 'البريد أو كلمة المرور غير صحيحة' : 'Invalid email or password'));
      } else {
        toast.error(err.response.data?.detail || t('error'));
      }
    } finally {
      setBusy(false);
    }
  };

  const seedDemo = async () => {
    try {
      await api.post('/admin/seed-demo-users');
      toast.success(t('saved'));
    } catch {
      toast.error(t('error'));
    }
  };

  const fillDemo = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <main id="main" className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t('auth_login_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('field_email')}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">{t('field_password')}</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="login-submit">
              {busy ? t('loading') : t('submit_login')}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-4 text-center">
            {t('no_account')} <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="goto-register">{t('nav_register')}</Link>
          </p>

          <div className="mt-6 border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground">{t('demo_accounts')}</span>
              <Button variant="ghost" size="sm" onClick={seedDemo} data-testid="seed-demo-btn" className="text-xs h-7">{t('seed_demo')}</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.role} onClick={() => fillDemo(d)} data-testid={`demo-${d.role}`} type="button" className="text-start text-xs border border-border rounded p-2 hover:bg-secondary">
                  <div className="font-semibold capitalize">{t('role_' + d.role)}</div>
                  <div className="text-muted-foreground truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
